// The store's sales: the numbers that matter over a period, the products that
// made them, and a chart that follows whatever is picked out. Admin → Store →
// Sales shows it over the whole store (aldoo-na `store {sales_report}`); a
// project shows it over its own scope by passing its own `api` (aldoo.com: a
// seller's organization) and its own words (`useTexts`).
//
// Views and conversion are counted where the project counts product views
// (aldoo-na/store/sales-report setViews); where it does not, the answers say
// `views: false` and the two are not shown. Commission and Earnings are a
// vendor's (aldoo-na/store/sales-report `orgId`: the payouts ledger): shown
// only where the answers carry them.
//
// It reads like the ads dashboard and the store consoles it was asked to
// resemble: a range and a unit above, cards that switch metrics on and off,
// a chart of the organization's totals, and a table underneath. Tick products
// and the chart stops showing the totals and shows those products instead,
// each in the colour its row carries — the App Store Connect / Analytics
// idea, where the selection is the legend.
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useUrlParam, useUrlFlag } from "aldoo-ra/URLState"
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts"
import moment from "moment"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { Field, Select, Section, Thumb, Toggle } from "./ui"
import useStoreEvents from "aldoo-ra/Store/useStoreEvents"
import { currencySymbol, useStoreCurrency } from "aldoo-ra/Store/currency"
import useSalesTexts from "./useSalesTexts"
import { storeSalesAPI } from "./api"
import FieldsPicker, { useFields } from "./FieldsPicker"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// Each metric has a colour of its own and the axis it belongs on: counts and
// money share the left one, a percentage would be a flat line beside them and
// gets the right. Money is in the store's currency (the report adds it up in
// it), handed to `format` as its second argument.
const cents = (v, currency) => `${currencySymbol(currency, "en")}${((v || 0) / 100).toFixed(2)}`
const METRICS = [
  { key: "views", label: "Views", color: "#3b6fd4", axis: "left", format: (v) => Math.round(v).toLocaleString() },
  // the keys are the API's; the labels are the shop's words for them
  { key: "sales", label: "Units", color: "#cc4436", axis: "left", format: (v) => Math.round(v).toLocaleString() },
  { key: "conversion", label: "Conversion", color: "#e8a33d", axis: "right", format: (v) => `${(v || 0).toFixed(2)}%` },
  // the dashboards' words: Gross sales, Refunded, Delivery refunds, Net sales
  { key: "revenue", label: "Gross sales", color: "#0f766e", axis: "left", format: cents },
  // orders to be paid on delivery and not paid yet, by the day placed: accounted for apart, Gross sales
  // once completed. Shown when the answers carry it (a store that sells on delivery); the totals' only, not a product's column
  { key: "processing", label: "Processing sales", color: "#b45309", axis: "left", format: cents, optional: true, chartOnly: true },
  // money given back in the period for goods, whenever they were sold: a fact of its own,
  // not a hole in the revenue
  { key: "refunded", label: "Refunded", color: "#8a3ab0", axis: "left", format: cents },
  // the goods' sales once their refunds are taken off — before Stripe's fees and any revenue share
  { key: "net", label: "Net sales", color: "#2f9e63", axis: "left", format: cents },
  // the delivery buyers paid, and what of it was given back: apart from the goods (a vendor's
  // delivery is theirs in full — Earnings = Net sales − Commission + Delivery − Delivery refunds).
  // The orders' own, not the products': the chart only
  { key: "delivery", label: "Delivery", color: "#0e7490", axis: "left", format: cents, chartOnly: true },
  { key: "refundedDelivery", label: "Delivery refunds", color: "#b0742a", axis: "left", format: cents },
  // a vendor's report only (optional: shown when the answers carry them) — the
  // store's commission on the sales and the vendor's part, as the payouts
  // ledger settled them
  { key: "commission", label: "Commission", color: "#64748b", axis: "left", format: cents, optional: true },
  { key: "earnings", label: "Earnings", color: "#4f46e5", axis: "left", format: cents, optional: true },
]
// the report as it opens: the last seven days, views and units
const DEFAULT_FILTER = { type: "preset", value: { days: 7 } }
const DEFAULT_METRICS = ["views", "sales"]

const metricOf = (key) => METRICS.find((m) => m.key === key) || METRICS[0]
// what only a store that counts product views has
const VIEW_METRICS = ["views", "conversion"]
// what only some answers have: on when the series' buckets carry it
const OPTIONAL_METRICS = METRICS.filter((m) => m.optional).map((m) => m.key)
const optionalIn = (buckets) => OPTIONAL_METRICS.filter((key) => buckets.some((bucket) => key in bucket && (!metricOf(key).whenAny || bucket[key] > 0)))
// the metrics that are counted; the rest are worked out from them
const DERIVED = ["conversion", "net"]
const SUMMED = METRICS.filter((m) => !DERIVED.includes(m.key)).map((m) => m.key)
// what a reader sees until they pick otherwise (the Fields button): the money given back
// and the gross are a click away, not in the way
const HIDDEN_BY_DEFAULT = ["revenue", "refunded", "refundedDelivery"]
// the cards fill one row on a wide screen whatever number are shown
const XL_COLS = { 1: "xl:grid-cols-1", 2: "xl:grid-cols-2", 3: "xl:grid-cols-3", 4: "xl:grid-cols-4", 5: "xl:grid-cols-5", 6: "xl:grid-cols-6", 7: "xl:grid-cols-7", 8: "xl:grid-cols-8" }
const DEFAULT_FIELDS = METRICS.map((m) => m.key).filter((key) => !HIDDEN_BY_DEFAULT.includes(key))
// the figures that came after the first picks were saved (FieldsPicker useFields)
const ADDED_FIELDS = ["commission", "earnings", "delivery", "processing"]
// several metrics may be drawn at once; a picked product's lines are told
// apart by the dash of the metric they carry
const DASHES = { processing: "5 2 1 2", views: undefined, sales: "6 3", conversion: "2 3", revenue: "10 4", refunded: "4 4", net: "1 3", delivery: "3 6", commission: "8 2 2 2", earnings: "12 3" }

const AGGREGATES = [
  { value: "hour", label: "Hours" },
  { value: "day", label: "Days" },
  { value: "week", label: "Weeks" },
  { value: "month", label: "Months" },
  { value: "quarter", label: "Quarters" },
  { value: "year", label: "Years" },
]

// the colours a picked product's line and its row share
const SERIES_COLORS = ["#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"]
const colorOf = (index) => SERIES_COLORS[index % SERIES_COLORS.length]

// how many picked products are named before the rest go behind a count
const CHIP_LIMIT = 10

// the dropdown's own presets, the same ids and values, so their names can be
// translated at render
const DATE_PRESETS = [
  { id: "last24h", label: "Last 24 Hours", value: { days: 1 } },
  { id: "last7d", label: "Last 7 Days", value: { days: 7 } },
  { id: "last30d", label: "Last 30 Days", value: { days: 30 } },
  { id: "last90d", label: "Last 90 Days", value: { days: 90 } },
  { id: "last365d", label: "Last 365 Days", value: { days: 365 } },
  { id: "lifetime", label: "Lifetime", value: null },
  { id: "custom", label: "Custom Range", value: "custom" },
]

const SELECTION_VIEWS = [
  { value: "all", label: "All products" },
  { value: "selected", label: "Selected only" },
]

/**
 * A range the API understands, from what the dropdown answers with:
 * `{ type: "preset", value: { days } | null }` — null is Lifetime — or
 * `{ type: "custom", range: { startDate, endDate } }`.
 */
// The range as calendar dates ("YYYY-MM-DD"): the report cuts them into days in the
// store's (or the vendor's) zone, the same days the dashboards count — "last 30 days" is
// 30 days with today, as there.
const dayOf = (date) => moment(date).format("YYYY-MM-DD")
const rangeOf = (filter) => {
  const end = dayOf(new Date())
  if (filter?.type === "custom" && filter.range?.startDate && filter.range?.endDate) {
    return { startDate: dayOf(filter.range.startDate), endDate: dayOf(filter.range.endDate) }
  }
  const days = filter?.value?.days
  // Lifetime has no start of its own: the report begins the chart at the
  // first view or sale there is, so an organization's history is not
  // preceded by years of empty buckets
  if (!days) return { lifetime: true, endDate: end }
  return { startDate: dayOf(moment().subtract(days - 1, "days")), endDate: end }
}

/** The table's search object (`{ field: term }`) as the term the report takes. */
const searchTermOf = (search) => {
  if (!search) return ""
  if (typeof search === "string") return search
  const values = Object.values(search).flatMap((value) => (Array.isArray(value) ? value.flatMap((entry) => Object.values(entry)) : [value]))
  return values.find((value) => typeof value === "string" && value.trim()) || ""
}

/**
 * `orgId`: the seller whose sales these are (the live updates follow it; null:
 * the whole store). `timeZone`: the zone the days are cut in, said under the
 * title. `api`: { report, series } (./api.js). `useTexts`: a hook answering t().
 */
export default function StoreSales({ orgId = null, timeZone, api = storeSalesAPI, useTexts = useSalesTexts, helpKeys = {} }) {
  const { currency } = useStoreCurrency()
  const t = useTexts()
  // whether the store counts product views: known from the first answer
  const [countsViews, setCountsViews] = useState(true)
  // the optional metrics the answers carry (none until they say)
  const [optional, setOptional] = useState([])
  const shownMetrics = useMemo(
    () => METRICS.filter((m) => (countsViews || !VIEW_METRICS.includes(m.key)) && (!m.optional || optional.includes(m.key))),
    [countsViews, optional.join()]
  )
  const datePresets = useMemo(() => DATE_PRESETS.map((preset) => ({ ...preset, label: t(preset.label) })), [t])
  const aggregateOptions = useMemo(() => AGGREGATES.map((option) => ({ ...option, label: t(option.label) })), [t])
  const selectionViews = useMemo(() => SELECTION_VIEWS.map((option) => ({ ...option, label: t(option.label) })), [t])
  // the last seven days: the question a vendor opens this tab with is what
  // happened lately, not what ever happened
  // what the report is set to lives in the address (sales_*), so a reload or a shared link keeps it
  const [filterParam, setFilterParam] = useUrlParam("sales_period", { default: "" })
  const filter = useMemo(() => {
    try {
      // a custom range's days come back as dates (the picker works with Date objects)
      return filterParam ? JSON.parse(filterParam, (key, v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v) ? new Date(v) : v)) : DEFAULT_FILTER
    } catch {
      return DEFAULT_FILTER
    }
  }, [filterParam])
  const setFilter = useCallback((next) => {
    const v = typeof next === "function" ? next(filter) : next
    setFilterParam(!v || JSON.stringify(v) === JSON.stringify(DEFAULT_FILTER) ? "" : JSON.stringify(v))
  }, [filter, setFilterParam])
  // below md the chart gives its width to the data: slimmer axes, no side margin
  const [phone, setPhone] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const update = () => setPhone(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])
  const [aggregate, setAggregate] = useUrlParam("sales_by", { default: "day" })
  // the figures each section shows, as the reader picked them (the Fields button)
  const [tileFields, setTileFields] = useFields("store-sales.chart-fields", DEFAULT_FIELDS, { added: ADDED_FIELDS })
  const [tableFields, setTableFields] = useFields("store-sales.table-fields", DEFAULT_FIELDS, { added: ADDED_FIELDS })
  const shownTiles = shownMetrics.filter((m) => tileFields.includes(m.key))
  const shownColumns = shownMetrics.filter((m) => tableFields.includes(m.key) && !m.chartOnly)
  // several metrics may be on at once, as in the ad consoles; the last one on
  // cannot be turned off — a chart of nothing says nothing
  const [metricsParam, setMetricsParam] = useUrlParam("sales_metrics", { default: DEFAULT_METRICS.join(",") })
  const metrics = useMemo(() => metricsParam.split(",").filter(Boolean), [metricsParam])
  const setMetrics = useCallback((next) => {
    const v = typeof next === "function" ? next(metrics) : next
    setMetricsParam((v.length ? v : DEFAULT_METRICS).join(","))
  }, [metrics, setMetricsParam])
  const [view, setView] = useUrlParam("sales_view", { default: "all" })
  // the products picked in the table (their pids)
  const [selectedParam, setSelectedParam] = useUrlParam("sales_products", { default: "" })
  const selected = useMemo(() => selectedParam.split(",").filter(Boolean), [selectedParam])
  const setSelected = useCallback((next) => {
    const v = typeof next === "function" ? next(selected) : next
    setSelectedParam((v || []).join(","))
  }, [selected, setSelectedParam])
  const [titles, setTitles] = useState({})
  // a picked product whose line is hidden stays picked: the chip is the switch
  const [hidden, setHidden] = useState([])
  const [showAllChips, setShowAllChips] = useState(false)
  // Groups folds the period onto the cycle of the unit — which hour of the
  // day, which day of the week, which month of the year sells — instead of
  // drawing it as a line through time. One metric at a time: bars of four
  // different things side by side answer nothing.
  const [grouped, setGrouped] = useUrlFlag("sales_grouped")
  const [series, setSeries] = useState({ buckets: [], series: [], unit: "day" })
  const [loading, setLoading] = useState(true)
  // a sale or a refund redraws the chart and the table under it: this is the
  // figure a shop watches while it trades
  const [live, setLive] = useState(0)
  useStoreEvents({ orgId, onEvent: () => setLive((tick) => tick + 1) })

  const range = useMemo(() => rangeOf(filter), [filter])
  // a figure taken off the cards is taken off the chart; the chart keeps at least one
  useEffect(() => {
    setMetrics((current) => {
      const kept = current.filter((key) => tileFields.includes(key))
      if (kept.length) return kept.length === current.length ? current : kept
      const first = shownMetrics.find((m) => tileFields.includes(m.key))
      return first ? [first.key] : current
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileFields, countsViews, optional.join()])
  const active = shownMetrics.filter((m) => metrics.includes(m.key))
  const toggleMetric = (key) =>
    setMetrics((current) => {
      if (grouped) return [key]
      if (current.includes(key)) return current.length > 1 ? current.filter((k) => k !== key) : current
      return [...current, key]
    })

  // turning Groups on keeps the first metric; turning it off leaves it on
  const toggleGrouped = (on) => {
    setGrouped(on)
    if (on) setMetrics((current) => current.slice(0, 1))
  }

  useEffect(() => {
    let alive = true
    setLoading(true)
    api.series({ company: orgId, pids: selected, aggregate, dateRange: range, group: grouped })
      .then((res) => {
        if (!alive) return
        const buckets = res?.buckets || []
        setSeries({ buckets, series: res?.series || [], unit: res?.unit || aggregate })
        if (buckets.length) setOptional((current) => {
          const next = optionalIn(buckets)
          return next.join() === current.join() ? current : next
        })
        if (res?.views !== false) return
        // no views here: the chart starts on what was sold
        setCountsViews(false)
        setMetrics((current) => {
          const kept = current.filter((key) => !VIEW_METRICS.includes(key))
          if (grouped) return kept.length ? kept : ["revenue"]
          return [...new Set([...kept, "sales", "revenue"])]
        })
      })
      .catch(() => alive && setSeries({ buckets: [], series: [], unit: aggregate }))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [orgId, range, aggregate, selected, grouped, live])

  // The totals of the period, from the same buckets the chart draws. Every
  // metric that adds up is summed by name, so a metric added to METRICS shows
  // its total without this having to be told about it — the previous list of
  // three left the newest card reading zero.
  const totals = useMemo(() => {
    const sum = Object.fromEntries(SUMMED.map((key) => [key, 0]))
    for (const bucket of series.buckets) for (const key of SUMMED) sum[key] += bucket[key] || 0
    // the derived two: a ratio, and what is left after the refunds
    return { ...sum, conversion: sum.views ? (sum.sales / sum.views) * 100 : 0, net: sum.revenue - sum.refunded }
  }, [series])

  const indexOfSelected = (pid) => selected.indexOf(pid)
  const toggleHidden = (pid) => setHidden((current) => (current.includes(pid) ? current.filter((id) => id !== pid) : [...current, pid]))
  const unselect = (pid) => {
    setSelected((current) => current.filter((id) => id !== pid))
    setHidden((current) => current.filter((id) => id !== pid))
  }

  /**
   * The table asks for its own page, sorted and searched — the report does
   * all three. The titles ride along so the chart's legend can name a
   * product that is not on the page any more.
   */
  const loadPage = async ({ page, itemsPerPage, search, sort }) => {
    const order = sort?.field ? `${sort.order === "asc" ? "" : "-"}${sort.field}` : countsViews ? "-views" : "-revenue"
    const term = searchTermOf(search)
    const remember = (rows) => setTitles((current) => ({ ...current, ...Object.fromEntries(rows.map((row) => [row.pid, row.title])) }))

    // "selected only" is a handful of products that may sit on any page of the
    // report: the picked ones are read in one go and paged here
    if (view === "selected") {
      if (!selected.length) return { data: [], total: 0 }
      const res = await api.report({ company: orgId, dateRange: range, page: 1, items_per_page: 500, order_by: order, search: term })
      const rows = (res?.data || []).filter((row) => selected.includes(row.pid))
      remember(rows)
      const from = (page - 1) * itemsPerPage
      return { data: rows.slice(from, from + itemsPerPage), total: rows.length }
    }

    const res = await api.report({ company: orgId, dateRange: range, page, items_per_page: itemsPerPage, order_by: order, search: term })
    const rows = res?.data || []
    remember(rows)
    return { data: rows, total: res?.total ?? rows.length }
  }

  /**
   * One column per bucket. A line is a metric (`views`) while nothing is
   * picked out, and a product and a metric (`<pid>::views`) once products
   * are: colour then says which product, and the dash which metric.
   */
  const chartData = useMemo(() => {
    // grouped: the buckets are the cycle's positions, one metric, and the
    // picked products (when there are any) are what the API counted
    if (grouped) return series.buckets.map((bucket) => ({ timestamp: bucket.timestamp, [metrics[0]]: bucket[metrics[0]] }))
    if (!selected.length) {
      return series.buckets.map((bucket) => ({ timestamp: bucket.timestamp, ...Object.fromEntries(metrics.map((key) => [key, bucket[key]])) }))
    }
    const byTimestamp = new Map()
    for (const line of series.series) {
      for (const bucket of line.buckets) {
        const row = byTimestamp.get(bucket.timestamp) || { timestamp: bucket.timestamp }
        for (const key of metrics) row[`${line.pid}::${key}`] = bucket[key]
        byTimestamp.set(bucket.timestamp, row)
      }
    }
    return [...byTimestamp.values()]
  }, [series, selected, metrics, grouped])

  /** The lines the chart draws. The chips name them, so there is no legend. */
  const lines = useMemo(() => {
    if (grouped) return active.slice(0, 1).map((m) => ({ key: m.key, name: t(m.label), color: m.color, axis: "left", metric: m.key }))
    if (!selected.length) return active.map((m) => ({ key: m.key, name: t(m.label), color: m.color, axis: m.axis, metric: m.key }))
    return selected.filter((pid) => !hidden.includes(pid)).flatMap((pid) =>
      active.map((m) => ({
        key: `${pid}::${m.key}`,
        name: active.length > 1 ? `${titles[pid] || pid} · ${t(m.label)}` : titles[pid] || pid,
        color: colorOf(selected.indexOf(pid)),
        dash: active.length > 1 ? DASHES[m.key] : undefined,
        axis: m.axis,
        metric: m.key,
      }))
    )
  }, [selected, hidden, active, titles, grouped, t])

  const hasRightAxis = lines.some((line) => line.axis === "right")

  // The tooltip: the value in the metric's own format. On a phone it cannot
  // follow the finger — a box of many product lines runs off the screen — so
  // it is pinned at the top of the plot, as wide as the plot, its names cut
  // short to fit.
  const formatter = (value, name, entry) => {
    const line = lines.find((item) => item.key === entry?.dataKey)
    return [metricOf(line?.metric).format(value, currency), name]
  }
  const tooltipProps = phone
    ? {
        formatter,
        position: { x: 0, y: 0 },
        allowEscapeViewBox: { x: false, y: false },
        wrapperStyle: { width: "100%", maxWidth: "100%", pointerEvents: "none" },
        contentStyle: { width: "100%", fontSize: 11, padding: "6px 8px", whiteSpace: "normal" },
        itemStyle: { padding: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        labelStyle: { fontSize: 11, marginBottom: 2 },
      }
    : { formatter }

  const titleOf = (pid) => titles[pid] || pid

  return (
    <div className="flex flex-col gap-4">
      <Section
        title={
          <span className="inline-flex items-center gap-1.5">
            {t`Sales`}
            {/* the help center's article on the report, where the report is (helpKeys.sales) */}
            <HelpHint helpKey={helpKeys.sales} iconClassName="w-[18px] h-[18px]" />
          </span>
        }
        subtitle={timeZone ? t("Days begin at midnight in %s", timeZone.replace(/_/g, " ")) : undefined}
        stackUntil="xl"
        actions={
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full md:w-[290px]"><DateFilterDropdown variant="admin" showLabel={false} defaultPreset="last7d" customPresets={datePresets} value={filter} onChange={setFilter} showArrows t={t} /></div>
            {/* on a phone: the dates, then the grouping (the whole width: its period in full), then Groups and Fields side by side */}
            <Field
              label={t`Aggregate by`}
              className="w-full md:w-[150px]"
              hint={series.unit !== aggregate ? t("Too long a range for %s — shown by %s", t(AGGREGATES.find((a) => a.value === aggregate)?.label).toLowerCase(), t(series.unit)) : undefined}
            >
              <Select value={aggregate} onChange={setAggregate} options={aggregateOptions} className="w-full" />
            </Field>
            <div className="w-full md:w-auto flex items-center justify-between gap-3 md:self-end md:min-h-[42px]">
              {/* last, because it says what to do with the two beside it: the box and its label alone */}
              <Toggle checked={grouped} onChange={toggleGrouped} label={t`Group the period`} data-testid="sales-groups" />
              <FieldsPicker fields={shownMetrics} value={tileFields} onChange={setTileFields} defaults={DEFAULT_FIELDS} t={t} title={t`Fields of the chart`} testId="sales-chart-fields" helpKey={helpKeys.fields} helpKeyOf={helpKeys.metric} />
            </div>
          </div>
        }
      >
        {/* The cards are the metric switches, and the chart's legend: a metric
            that is on fills its card with its own colour, the way the ad
            consoles show them. */}
        <div className={`grid grid-cols-2 md:grid-cols-3 ${XL_COLS[Math.min(shownTiles.length, 8)] || "xl:grid-cols-4"} gap-3`}>
          {shownTiles.map((m) => {
            const on = metrics.includes(m.key)
            return (
              <button
                key={m.key}
                onClick={() => toggleMetric(m.key)}
                // the label at the top (one line or two), the figure held to the bottom left: every card of
                // a row as tall as the tallest, the figures on one line whatever the labels do
                className={`h-full flex flex-col justify-between gap-1 rounded-lg px-4 py-3 text-left transition-colors ${on ? "text-white" : "border border-admin_bordered text-admin_text hover:bg-admin_neutral_1/50"}`}
                style={on ? { background: m.color } : undefined}
                aria-pressed={on}
                title={on ? t("Hide %s from the chart", t(m.label)) : t("Show %s on the chart", t(m.label))}
              >
                <div className={`text-xs flex items-start gap-1.5 ${on ? "text-white/80" : "text-admin_text_secondary"}`}>
                  <span
                    className="inline-block w-2 h-2 mt-[5px] shrink-0 rounded-full"
                    style={{ background: on ? "rgba(255,255,255,0.9)" : m.color }}
                  />
                  {t(m.label)}
                  {/* what the figure is, from the help center (helpKeys.metric(key)) */}
                  {helpKeys.metric && <HelpHint helpKey={helpKeys.metric(m.key)} className="ml-auto -mr-1 -mt-0.5" />}
                </div>
                <div className={`text-2xl font-semibold tabular-nums ${on ? "text-white" : "text-admin_text"}`}>
                  {loading ? <span className={on ? "text-white/70" : "text-admin_text_secondary"}>…</span> : m.format(totals[m.key], currency)}
                </div>
              </button>
            )
          })}
        </div>

        {/* on a phone the chart runs to the section's edges, with slim axes */}
        <div className="h-[280px] -mx-4 md:mx-0">
          {loading ? (
            <div className="h-full flex items-center justify-center gap-3 text-admin_text_secondary">
              <Spinner variant="admin_primary" size="md" />
              {t`Loading…`}
            </div>
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            {grouped ? (
              // a distribution reads as bars: each one a position of the cycle
              <BarChart data={chartData} margin={{ top: 8, right: phone ? 4 : 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-admin_bordered" vertical={false} />
                <XAxis dataKey="timestamp" tick={{ fontSize: 11 }} interval={0} minTickGap={2} />
                <YAxis tick={{ fontSize: 11 }} width={phone ? 36 : 52} allowDecimals={false} />
                <Tooltip cursor={{ fill: "currentColor", opacity: 0.06 }} {...tooltipProps} />
                {lines.map((line) => (
                  <Bar key={line.key} dataKey={line.key} name={line.name} fill={line.color} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                ))}
              </BarChart>
            ) : (
            <LineChart data={chartData} margin={{ top: 8, right: phone ? 4 : 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-admin_bordered" vertical={false} />
              <XAxis dataKey="timestamp" tick={{ fontSize: 11 }} minTickGap={24} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={phone ? 36 : 52} allowDecimals={false} />
              {hasRightAxis && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={phone ? 36 : 52} unit="%" />}
              <Tooltip {...tooltipProps} />
              {lines.map((line) => (
                <Line
                  key={line.key}
                  yAxisId={line.axis === "right" && hasRightAxis ? "right" : "left"}
                  type="monotone"
                  dataKey={line.key}
                  name={line.name}
                  stroke={line.color}
                  strokeDasharray={line.dash}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
            )}
          </ResponsiveContainer>
          )}
        </div>
      </Section>

      <Section
        title={t`Products`}
        actions={
          <div className="flex items-center gap-3">
            {selected.length > 0 && (
              <button onClick={() => { setSelected([]); setHidden([]) }} className="text-xs text-admin_text_secondary hover:text-admin_primary whitespace-nowrap">
                {t("Clear selection (%s)", selected.length)}
              </button>
            )}
            <div className="flex-1 md:flex-none md:w-[170px]"><Select value={view} onChange={setView} options={selectionViews} className="w-full" /></div>
            <FieldsPicker fields={shownMetrics.filter((m) => !m.chartOnly)} value={tableFields} onChange={setTableFields} defaults={DEFAULT_FIELDS} t={t} title={t`Columns of the table`} testId="sales-table-fields" />
          </div>
        }
      >
        {/* The picked products, which is what the chart draws — so it needs no
            legend of its own. A chip switches its line off without letting the
            product go; the × lets it go. Ten at a time, the rest behind a
            count, because a hundred of them is a wall, not a legend. */}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {(showAllChips ? selected : selected.slice(0, CHIP_LIMIT)).map((pid) => {
              const index = selected.indexOf(pid)
              const off = hidden.includes(pid)
              return (
                <span
                  key={pid}
                  className={`group inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                    off ? "border-admin_bordered text-admin_text_secondary" : "border-transparent bg-admin_neutral_1 text-admin_text"
                  }`}
                  data-testid={`series-chip-${pid}`}
                >
                  <button
                    onClick={() => toggleHidden(pid)}
                    className="inline-flex items-center gap-1.5 max-w-[240px]"
                    title={off ? t("Show %s on the chart", titleOf(pid)) : t("Hide %s from the chart", titleOf(pid))}
                    aria-pressed={!off}
                  >
                    <span
                      className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ background: off ? "transparent" : colorOf(index), border: `1px solid ${colorOf(index)}` }}
                    />
                    <span className={`truncate ${off ? "line-through" : ""}`}>{titleOf(pid)}</span>
                  </button>
                  <button
                    onClick={() => unselect(pid)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-admin_text_secondary hover:text-admin_error"
                    title={t("Remove %s from the selection", titleOf(pid))}
                    aria-label={t("Remove %s", titleOf(pid))}
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              )
            })}
            {selected.length > CHIP_LIMIT && (
              <button
                onClick={() => setShowAllChips((current) => !current)}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border border-admin_bordered text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary transition-colors"
              >
                {showAllChips ? t`Show less` : t("+%s more", selected.length - CHIP_LIMIT)}
              </button>
            )}
          </div>
        )}
        <DataTable
          variant="admin"
          apiMethod={loadPage}
          apiDataField="data"
          itemIdField="pid"
          refreshKey={`${orgId}:${range.startDate}:${range.endDate}:${view}:${selected.join(",")}:${live}`}
          itemsPerPage={10}
          searchPlaceholder={t`Search products…`}
          noData={<Typography variant="h3" className="text-admin_text">{t`No data available`}</Typography>}
          searchField="title pid"
          enableSelection
          selectedItems={selected}
          onSelectionChange={(ids) => setSelected(ids)}
          // a picked row wears the colour of its line, so the chart and the
          // table read as one thing
          styleOverrides={{
            getRowStyle: (index, item) => {
              const picked = indexOfSelected(item?.pid)
              return picked === -1 ? "" : "bg-admin_primary/5"
            },
            getCardStyle: (index, item) => (indexOfSelected(item?.pid) === -1 ? "" : "bg-admin_primary/5 border-admin_primary/40"),
          }}
          renders={[
            {
              id: "title",
              title: t`Product`,
              sortable: true,
              render: (title, item) => {
                const picked = indexOfSelected(item.pid)
                return (
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-1 h-8 rounded-full shrink-0" style={{ background: picked === -1 ? "transparent" : colorOf(picked) }} />
                    <Thumb src={item.image} name={title} className="w-[50px] h-[50px] rounded shrink-0" />
                    <div className="min-w-0">
                      <div className="truncate text-admin_text">{title}</div>
                      <div className="text-xs text-admin_text_secondary truncate">{item.pid}</div>
                    </div>
                  </div>
                )
              },
            },
            ...shownColumns.map((m) => ({
              id: m.key,
              title: t(m.label),
              sortable: true,
              headerClassName: "w-[120px] text-right",
              render: (value) => <span className="block text-right tabular-nums text-admin_text">{m.format(value, currency)}</span>,
            })),
          ]}
        />
      </Section>
    </div>
  )
}

export { METRICS, AGGREGATES }
